/**
 * The Commissioner's "Still to pick" card on the Results tab (ISSUE-1328),
 * the web twin of mobile's nudge bar on `PoolStandingsScreen.tsx`.
 *
 * Three reads and one write:
 *
 *  - `POST /round/list`: the first round that has not locked, the only round
 *    anyone can still be nudged about. `locked` is false for a scheduled round
 *    too, so this is a candidate, not yet the open round.
 *  - `POST /round/unpicked-list`: who has not picked. Its `roundStatus` is the
 *    server's ruling on whether the round is open. A scheduled round answers
 *    an empty list, and without that check the card would tell a Commissioner
 *    that everyone had picked a round nobody can pick in yet.
 *  - The server refuses a plain member with FORBIDDEN_ROLE. The pane only
 *    mounts this card for an owner or an Admin, which spares a member that
 *    refusal, but the server's answer is still the gate: no list, no card.
 *  - `POST /round/nudge-entry`: one nudge to one member. Already nudged this
 *    round and round no longer open are answered on the row, in words; any
 *    other refusal goes through the shared error modal.
 */
import {
  useRoundList,
  useRoundNudgeEntry,
  useRoundUnpickedList,
} from "@sportsmet/contest-sdk/private";
import { Button } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import { initialsFor } from "./poolStandingsModel";

export const NUDGE_SECTION_TITLE = "Still to pick";
export const NUDGE_ACTION_LABEL = "Nudge";
export const NUDGE_SENT_LINE = "Nudge sent";
export const NUDGE_EVERYONE_PICKED_LINE = "Everyone has picked this round.";
export const NUDGE_ALREADY_SENT_LINE = "Already nudged this round";
export const NUDGE_ROUND_NOT_OPEN_LINE =
  "The round is not open, so there is nobody to nudge.";

export function nudgeSectionTitle(count: number): string {
  return `${NUDGE_SECTION_TITLE} (${String(count)})`;
}

type typeNudgeError =
  { code?: string; details?: Record<string, unknown> } | null | undefined;

/**
 * The line a nudge leaves on its row, or null when the error is one the
 * shared modal has to explain instead.
 */
export function nudgeRowLine(error: typeNudgeError): string | null {
  if (error === null || error === undefined) return NUDGE_SENT_LINE;
  if (error.code === "NUDGE_ALREADY_SENT") return NUDGE_ALREADY_SENT_LINE;
  if (error.code === "CONFLICT" && error.details?.reason === "roundNotOpen") {
    return NUDGE_ROUND_NOT_OPEN_LINE;
  }
  return null;
}

export const UnpickedNudgeList = ({
  poolId,
  contestId,
}: {
  poolId: string;
  contestId: string;
}): ReactNode => {
  const roundList = useRoundList(
    { poolId, contestId, limit: 500 },
    { enabled: contestId !== "" },
  );
  const candidateRoundId =
    roundList.data?.data?.items.find((item) => !item.locked)?.roundId ?? null;

  const unpicked = useRoundUnpickedList(
    { poolId, roundId: candidateRoundId ?? "" },
    { enabled: candidateRoundId !== null, staleTime: 0 },
  );
  const answer = unpicked.data?.data ?? null;
  const isOpen = answer !== null && answer.roundStatus === "open";

  const nudge = useRoundNudgeEntry();
  const [pendingEntryId, setPendingEntryId] = useState<string | null>(null);
  const [rowLines, setRowLines] = useState<Record<string, string>>({});

  if (!isOpen || candidateRoundId === null) return null;

  async function handleNudge(entryId: string): Promise<void> {
    if (candidateRoundId === null) return;
    setPendingEntryId(entryId);
    try {
      const result = await nudge.mutateAsync({
        poolId,
        roundId: candidateRoundId,
        entryId,
      });
      const line = nudgeRowLine(result.error ?? null);
      if (line === null) {
        showApiError(result);
        return;
      }
      setRowLines((previous) => ({ ...previous, [entryId]: line }));
    } finally {
      setPendingEntryId(null);
    }
  }

  return (
    <section
      className="flex flex-col gap-2 rounded-lg border border-hairline p-4"
      data-testid="unpicked-nudge"
    >
      <h3 className="text-body text-ink">
        {nudgeSectionTitle(answer.items.length)}
      </h3>
      {answer.items.length === 0 ? (
        <p
          className="text-caption text-ink-secondary"
          data-testid="unpicked-nudge-everyone"
        >
          {NUDGE_EVERYONE_PICKED_LINE}
        </p>
      ) : (
        <ul className="flex flex-col">
          {answer.items.map((person) => {
            const line = rowLines[person.entryId];
            return (
              <li
                key={person.entryId}
                className="flex min-h-touch-target items-center gap-3 border-t border-hairline py-2"
                data-testid={`unpicked-nudge-row-${person.entryId}`}
              >
                {person.photoUrl ? (
                  <img
                    src={person.photoUrl}
                    alt=""
                    className="size-8 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-elevated text-caption text-ink">
                    {initialsFor(person.displayName)}
                  </span>
                )}
                <span className="min-w-0 flex-1 break-words text-body text-ink">
                  {person.displayName}
                </span>
                {line === undefined ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={pendingEntryId === person.entryId}
                    loadingLabel="Sending nudge"
                    data-testid={`unpicked-nudge-${person.entryId}`}
                    onClick={() => {
                      void handleNudge(person.entryId);
                    }}
                  >
                    {NUDGE_ACTION_LABEL}
                  </Button>
                ) : (
                  <span
                    role="status"
                    className="shrink-0 text-caption text-ink-secondary"
                    data-testid={`unpicked-nudge-line-${person.entryId}`}
                  >
                    {line}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
